import { Link } from "react-router-dom";
import { API_URL } from "@/config/api";
import { SITE_URL, PLAY_STORE_URL } from "@/config/site";
import { MODEL_COUNT, PROVIDER_COUNT } from "@/data/modelStats";
import { LANGUAGE_COUNT, languages } from "@/i18n";
import { LANG_SAMPLES } from "@/data/langSamples";
import SEO from "@/components/SEO";
import StickyCTABar from "@/components/StickyCTABar";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSelector from "@/components/LanguageSelector";
import { PLAN_PRICES } from "@/data/plans";
import ThemeToggle from "@/components/ThemeToggle";
import { useModelCount } from "@/hooks/useModelCount";
import { useLocalePrefix } from "@/hooks/useLocalePrefix";
import {
  MessageSquare,
  FileText,
  Image,
  Zap,
  Shield,
  Lock,
  Users,
  ArrowRight,
  Check,
  X,
  Menu,
  Smartphone,
  Bot,
  Sparkles,
  Mail,
  Loader2,
  Columns,
  Globe,
  Search,
  Headphones,
  Eye,
  Code2,
  Presentation,
  Briefcase,
  Megaphone,
  GraduationCap,
  Rocket,
  Link2 as LinkIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";

// Hero "Ensemble" orbit — the signature figure of the redesign: a calm core
// showing the live model count, ringed by drifting provider nodes ("one
// subscription, many minds"). Purely decorative → aria-hidden. Provider names +
// brand dot colors are invariants (no translation). Positions are the mockup's.
const ENSEMBLE_ORBIT_NODES = [
  { name: "GROQ", color: "#F0663E", top: "14%", left: "24%", delay: "0s" },
  { name: "Gemini", color: "#4285F4", top: "10%", left: "70%", delay: ".6s" },
  { name: "Mistral", color: "#E8A33D", top: "42%", left: "92%", delay: "1.1s" },
  {
    name: "OpenRouter",
    color: "#6D5DF6",
    top: "78%",
    left: "80%",
    delay: "1.6s",
  },
  {
    name: "Cloudflare",
    color: "#F38020",
    top: "90%",
    left: "38%",
    delay: "2.1s",
  },
  {
    name: "NVIDIA NIM",
    color: "#76B900",
    top: "64%",
    left: "8%",
    delay: "2.6s",
  },
  {
    name: "HuggingFace",
    color: "#FFD21E",
    top: "30%",
    left: "6%",
    delay: "3.1s",
  },
];

const EnsembleOrbit = ({ t, modelCount }) => (
  <div
    className="ensemble-orbit mt-16 sm:mt-20"
    aria-hidden="true"
    data-testid="hero-ensemble-orbit"
  >
    <svg
      className="orbit-rings"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
    >
      <g className="ring-spin">
        <circle
          cx="50"
          cy="50"
          r="32"
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="0.4"
          strokeDasharray="1.4 2.4"
        />
      </g>
      <g className="ring-spin rev">
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="0.4"
          strokeDasharray="1 3"
        />
      </g>
    </svg>
    <div className="orbit-core">
      <div className="orbit-k font-heading">{modelCount}</div>
      <div className="orbit-kk">
        {t("landing.orbit.core", "modèles orchestrés")}
      </div>
    </div>
    {ENSEMBLE_ORBIT_NODES.map((n) => (
      <div
        key={n.name}
        className="orbit-node drift"
        style={{ top: n.top, left: n.left, animationDelay: n.delay }}
      >
        <i style={{ background: n.color }} />
        {n.name}
      </div>
    ))}
  </div>
);

const LandingPage = () => {
  const landingJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: "Stronger Bot",
        url: SITE_URL,
        applicationCategory: "ProductivityApplication",
        operatingSystem: "Web, iOS, Android",
        // App is live on Google Play — tell crawlers it's installable there.
        downloadUrl: PLAY_STORE_URL,
        installUrl: PLAY_STORE_URL,
        description:
          "Plateforme IA tout-en-un : " +
          MODEL_COUNT +
          "+ modèles dont Llama 3.3 70B, Llama 4 Maverick, Gemini, Mistral, DeepSeek R1, Qwen 3 235B, GPT-OSS 120B (via HuggingFace). " +
          PROVIDER_COUNT +
          " providers, analyse de documents, agents spécialisés, voix, images HD, deep research. 83+ fonctionnalités, 15 langues.",
        inLanguage: ["fr", "en"],
        offers: [
          {
            "@type": "Offer",
            name: "Free",
            price: String(PLAN_PRICES.free),
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
            description: "50 messages/mois, 2 documents, chat IA basique",
          },
          {
            "@type": "Offer",
            name: "Starter",
            price: String(PLAN_PRICES.starter),
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
            description: "500 messages/mois, 10 documents, AI Writing Tools",
          },
          {
            "@type": "Offer",
            name: "Pro",
            price: String(PLAN_PRICES.pro),
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
            description:
              "2000 messages/mois, documents illimités, génération d'images",
          },
          {
            "@type": "Offer",
            name: "Business",
            price: String(PLAN_PRICES.business),
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
            description: "Messages illimités, documents illimités, API Access",
          },
          {
            "@type": "Offer",
            name: "Enterprise",
            price: String(PLAN_PRICES.enterprise),
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
            description: "Tout illimité, SLA garanti, account manager",
          },
        ],
        screenshot: `${SITE_URL}/icons/og-image.png`,
        featureList: [
          "Chat IA multi-modèle (Llama 3.3, Llama 4, Gemini, Mistral, DeepSeek)",
          "Analyse de documents PDF, Word, Excel, PowerPoint",
          "Génération d'images avec FLUX.1-schnell",
          "Reconnaissance vocale Speech-to-Text (Whisper)",
          "Mémoire conversationnelle persistante",
          "Assistants personnalisés",
        ],
        publisher: {
          "@type": "Organization",
          name: "Stronger Bot",
          url: SITE_URL,
        },
      },
      {
        "@type": "Organization",
        name: "Stronger Bot",
        url: SITE_URL,
        logo: `${SITE_URL}/icons/og-image.png`,
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          availableLanguage: ["French", "English"],
        },
      },
      {
        "@type": "WebSite",
        name: "Stronger Bot",
        url: SITE_URL,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Qu'est-ce que Stronger Bot ?",
            acceptedAnswer: {
              "@type": "Answer",
              text:
                "Stronger Bot est une plateforme IA tout-en-un qui regroupe " +
                MODEL_COUNT +
                "+ modèles dont Llama 3.3 70B, Llama 4 Maverick, Gemini, Mistral, DeepSeek R1, Qwen 3 235B et GPT-OSS 120B (via HuggingFace) dans un seul abonnement, répartis sur " +
                PROVIDER_COUNT +
                " providers. Au lieu d'empiler des souscriptions à $20/mois chez chaque fournisseur, tu paies €19/mois pour tous les avoir.",
            },
          },
          {
            "@type": "Question",
            name: "Est-ce gratuit ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui, le plan Free inclut 50 messages par mois, 2 documents et un chat IA basique. Sans carte bancaire. Tu passes sur un plan payant (Starter €9, Pro €19, Business €39) seulement si tu as besoin de plus.",
            },
          },
          {
            "@type": "Question",
            name: "En quoi Stronger Bot est différent de ChatGPT ?",
            acceptedAnswer: {
              "@type": "Answer",
              text:
                "ChatGPT te donne 1 modèle (GPT-4o) pour $20/mois. Stronger Bot t'en donne " +
                MODEL_COUNT +
                "+ pour €19/mois, avec génération d'images, mode vocal, analyse de documents, agents personnalisés et accès API inclus. Tu choisis le meilleur modèle pour chaque tâche au lieu d'être bloqué avec un seul.",
            },
          },
          {
            "@type": "Question",
            name: "Mes données sont-elles protégées ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui. L'application est hébergée en Europe (Cloudflare Pages) et respecte le RGPD nativement. Tes conversations et documents ne sont jamais utilisés pour entraîner des modèles. Tu peux exporter ou supprimer toutes tes données à tout moment.",
            },
          },
          {
            "@type": "Question",
            name: "Puis-je utiliser Stronger Bot sur mobile ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui, l'application web fonctionne sur tout navigateur mobile et peut s'installer comme une PWA. Les apps natives iOS et Android sont en cours de publication sur l'App Store et le Play Store.",
            },
          },
          {
            "@type": "Question",
            name: "Y a-t-il une période d'essai pour les plans payants ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui, tous les plans payants (Starter, Pro, Business, Enterprise) incluent 7 jours d'essai gratuit. Tu n'es facturé qu'à la fin de la période d'essai, et tu peux annuler à tout moment avant.",
            },
          },
        ],
      },
    ],
  };

  const { isAuthenticated, token } = useAuth();
  const { t } = useTranslation();

  // Beta-tester quick-access chip — mirrors the /pricing chip pattern
  // (CLAUDE.md §7): codes stay off public surfaces, but eligible users
  // get a discreet link to their dashboard right at the top of the hero
  // when they're authenticated. Non-beta visitors see nothing.
  const [isBetaTester, setIsBetaTester] = useState(false);
  // Mobile nav drawer — the five section anchors are `hidden md:flex` on
  // desktop; on phones (§E mobile-first) they'd otherwise be unreachable, so
  // a hamburger toggles this same list. Single source for both surfaces.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Product Hunt badge must match the active theme (the app is light-default
  // with a dark toggle — a hardcoded dark badge looked wrong on the light page).
  // SSR-safe: start at "light" (the default), then read + observe the resolved
  // theme (data-theme on <html>) client-side so it also flips with the toggle.
  const [phBadgeTheme, setPhBadgeTheme] = useState("light");
  useEffect(() => {
    const read = () =>
      setPhBadgeTheme(
        document.documentElement.getAttribute("data-theme") === "dark"
          ? "dark"
          : "light"
      );
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => obs.disconnect();
  }, []);
  const navLinks = [
    { href: "#catalog", label: t("nav.models") },
    { href: "#product", label: t("nav.product", "Produit") },
    { href: "#langues", label: t("nav.languages", "Langues") },
    { href: "#pricing", label: t("nav.pricing") },
    { href: "#comparison", label: t("nav.compare", "Comparer") },
  ];
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/beta/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setIsBetaTester(!!r.data?.is_beta))
      .catch(() => {
        // Silent — chip is opportunistic, not load-bearing.
      });
  }, [token]);

  // Live model + provider counts from /api/models. Trust-bar reads from
  // this; static SEO copy (meta description, JSON-LD) keeps its own
  // count comes from the MODEL_COUNT single source of truth.
  const modelCount = useModelCount({
    models: MODEL_COUNT,
    providers: PROVIDER_COUNT,
  });

  const features = [
    {
      icon: Columns,
      title: t("landing.features.compareModels.title"),
      description: t("landing.features.compareModels.description"),
    },
    {
      icon: MessageSquare,
      title: t("landing.features.chat.title"),
      description: t("landing.features.chat.description"),
    },
    {
      icon: FileText,
      title: t("landing.features.documents.title"),
      description: t("landing.features.documents.description"),
    },
    {
      icon: Search,
      title: t("landing.features.deepResearch.title"),
      description: t("landing.features.deepResearch.description"),
    },
    {
      icon: Globe,
      title: t("landing.features.multilingual.title"),
      description: t("landing.features.multilingual.description"),
    },
    {
      icon: Image,
      title: t("landing.features.images.title"),
      description: t("landing.features.images.description"),
    },
    {
      icon: Zap,
      title: t("landing.features.fast.title"),
      description: t("landing.features.fast.description"),
    },
    {
      icon: Shield,
      title: t("landing.features.security.title"),
      description: t("landing.features.security.description"),
    },
    {
      icon: Users,
      title: t("landing.features.assistants.title"),
      description: t("landing.features.assistants.description"),
    },
  ];

  const plans = [
    {
      id: "free",
      name: t("plans.free.name"),
      price: `${PLAN_PRICES.free}€`,
      features: t("plans.free.features", { returnObjects: true }),
      popular: false,
    },
    {
      id: "starter",
      name: t("plans.starter.name"),
      price: `${PLAN_PRICES.starter}€`,
      period: t("landing.pricing.perMonth"),
      features: t("plans.starter.features", { returnObjects: true }),
      popular: false,
    },
    {
      id: "pro",
      name: t("plans.pro.name"),
      price: `${PLAN_PRICES.pro}€`,
      period: t("landing.pricing.perMonth"),
      features: t("plans.pro.features", { returnObjects: true }),
      popular: true,
    },
    {
      id: "business",
      name: t("plans.business.name"),
      price: `${PLAN_PRICES.business}€`,
      period: t("landing.pricing.perMonth"),
      features: t("plans.business.features", { returnObjects: true }),
      popular: false,
    },
    {
      id: "enterprise",
      name: t("plans.enterprise.name"),
      price: `${PLAN_PRICES.enterprise}€`,
      period: t("landing.pricing.perMonth"),
      features: t("plans.enterprise.features", { returnObjects: true }),
      popular: false,
    },
  ];

  return (
    <>
      <div className="min-h-screen bg-background">
        <SEO
          title={`Stronger Bot — ${MODEL_COUNT} modèles IA en un seul abonnement`}
          description={`${MODEL_COUNT}+ modèles d'IA (Llama, Mistral, Gemini, DeepSeek, Qwen) en un seul abonnement dès ${PLAN_PRICES.starter} €/mois. Chat, analyse de documents, génération d'images et voix — en ${LANGUAGE_COUNT} langues.`}
          jsonLd={landingJsonLd}
          canonical="/"
        />
        {/* Skip link — RGAA 12.7 / WCAG 2.4.1 */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary-strong focus:text-primary-strong-foreground focus:shadow-lg focus-visible:ring-2 focus-visible:ring-white"
        >
          {t("a11y.skipToContent", "Aller au contenu")}
        </a>
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-40 glass safe-area-top">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link
                to="/"
                className="flex items-center gap-2 shrink-0"
                data-testid="logo-link"
              >
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading font-bold text-xl text-white whitespace-nowrap">
                  Stronger Bot
                </span>
              </Link>

              {/* Nav links match the validated "Ensemble" maquette: five
                  in-page anchors to the real landing sections (not the old
                  generic Features/Pricing/Plans). Each href targets an existing
                  section id — #catalog / #product / #langues / #pricing /
                  #comparison — so no anchor is dead. */}
              <div className="hidden md:flex items-center gap-6">
                {navLinks.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    className="text-slate-400 hover:text-white transition-colors rounded-md px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  >
                    {l.label}
                  </a>
                ))}
              </div>

              <div className="flex items-center gap-1.5 sm:gap-3">
                <ThemeToggle />
                <LanguageSelector variant="ghost" />
                {isAuthenticated ? (
                  <Link to="/dashboard">
                    <Button
                      className="btn-glow bg-primary-strong hover:bg-primary-strong/90"
                      data-testid="dashboard-btn"
                    >
                      {t("nav.dashboard")}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    {/* Login is de-emphasised and hidden on the smallest screens
                        (reachable from the hero CTA / footer) so the primary
                        "register" CTA always fits at 320–375px without crowding
                        the theme + language controls. */}
                    <Link to="/login" className="hidden sm:block">
                      <Button
                        variant="ghost"
                        className="text-slate-300 hover:text-white"
                        data-testid="login-btn"
                      >
                        {t("nav.login")}
                      </Button>
                    </Link>
                    <Link to="/register">
                      <Button
                        className="btn-glow bg-primary-strong hover:bg-primary-strong/90"
                        data-testid="register-btn"
                      >
                        {t("nav.register")}
                        <Sparkles className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </>
                )}
                {/* Hamburger — mobile only. Opens the section-anchor list that
                    is `hidden md:flex` on desktop. ≥44pt touch target. */}
                <button
                  type="button"
                  className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  aria-label={t("nav.openMenu", "Menu")}
                  aria-expanded={mobileNavOpen}
                  aria-controls="landing-mobile-nav"
                  onClick={() => setMobileNavOpen((v) => !v)}
                >
                  {mobileNavOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile nav drawer — same five anchors as desktop, single source
                (navLinks). Closes on selection so the jump lands cleanly. */}
            {mobileNavOpen && (
              <div
                id="landing-mobile-nav"
                className="md:hidden pb-3 pt-1 flex flex-col gap-1 border-t border-white/10"
              >
                {navLinks.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setMobileNavOpen(false)}
                    className="min-h-[44px] flex items-center px-2 rounded-md text-slate-300 hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <section
          id="main-content"
          className="relative pt-32 pb-20 px-4 overflow-hidden"
        >
          {/* Background glow */}
          <div className="absolute inset-0 hero-gradient" />

          <div className="max-w-7xl mx-auto text-center relative z-10">
            {/* Ensemble hero — two columns on desktop (message left, the
                Ensemble orbit right), stacking to one centered column on
                mobile (§E: 375px OK). The below-grid strip (quick actions,
                trust, stats) stays full-width + centered. */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center lg:text-left">
              <div className="min-w-0">
                {/* Beta-tester quick-access chip — visible only to authenticated
                beta testers. Sits above the brand badge so it's the first
                thing they see on /. Mobile-first: ≥44pt touch target,
                wraps cleanly at 375px, no hover-only state. */}
                {isBetaTester && (
                  <Link
                    to="/dashboard/beta"
                    className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 hover:bg-accent/20 active:bg-accent/25 transition-colors mb-4 min-h-[44px]"
                    data-testid="landing-beta-chip"
                  >
                    <Sparkles className="w-4 h-4 text-accent" />
                    <span className="text-sm text-slate-200">
                      {t(
                        "landing.betaChipLabel",
                        "Tu es Lifetime Beta Tester ·"
                      )}
                    </span>
                    <span className="text-sm text-accent font-medium">
                      {t("landing.betaChipCta", "Voir tes codes")}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-accent group-hover:translate-x-0.5 group-active:translate-x-0.5 transition-transform" />
                  </Link>
                )}
                <div className="animate-in-up inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm text-slate-300">
                    {t("landing.badge")}
                  </span>
                </div>

                {/* Product Hunt badge — uncomment PRODUCT_HUNT_POST_ID after launch */}
                {process.env.REACT_APP_PRODUCT_HUNT_POST_ID && (
                  <div className="mb-6">
                    <a
                      href={`https://www.producthunt.com/posts/stronger-bot?utm_source=badge-featured`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=${process.env.REACT_APP_PRODUCT_HUNT_POST_ID}&theme=${phBadgeTheme}`}
                        alt="Stronger Bot - Featured on Product Hunt"
                        width="250"
                        height="54"
                        loading="lazy"
                      />
                    </a>
                  </div>
                )}

                <h1
                  className="animate-in-up font-heading text-fluid-hero font-bold text-white mb-6"
                  style={{ "--d": "80ms" }}
                >
                  {t("landing.title")}
                  <br />
                  <span className="text-accent-gradient">
                    {t("landing.titleHighlight")}
                  </span>
                </h1>

                <p
                  className="animate-in-up text-fluid-lead text-slate-400 max-w-2xl mx-auto lg:mx-0 mb-10"
                  style={{ "--d": "160ms" }}
                >
                  {t("landing.subtitle")}
                </p>

                <div
                  className="animate-in-up flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
                  style={{ "--d": "240ms" }}
                >
                  <Link to="/register">
                    <Button
                      size="lg"
                      className="btn-glow bg-primary-strong hover:bg-primary-strong/90 text-lg px-8 py-6"
                      data-testid="hero-cta"
                    >
                      {t("landing.cta")}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <a href="#features">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5 text-lg px-8 py-6"
                    >
                      {t("landing.discover")}
                    </Button>
                  </a>
                </div>
              </div>
              {/* Right column — the Ensemble orbit (stacks below on mobile) */}
              <div className="mt-12 lg:mt-0">
                <EnsembleOrbit t={t} modelCount={modelCount.models} />
              </div>
            </div>

            {/* Quick-action chips — kimi-inspired modular product entries.
                Each chip surfaces a concrete \"I can do X in 5 seconds\"
                affordance above the fold. They link to /register so the
                discovery moment immediately becomes a signup intent (the
                value-prop is the action itself, not a feature page). */}
            <div
              className="mt-8 flex flex-wrap items-center justify-center gap-2 max-w-3xl mx-auto"
              data-testid="hero-quick-actions"
              aria-label={t("landing.quickActions.label", "Actions populaires")}
            >
              <span className="text-xs uppercase tracking-wider text-slate-400 mr-1 hidden sm:inline">
                {t("landing.quickActions.prefix", "En 5 sec :")}
              </span>
              {[
                {
                  id: "compare",
                  icon: Columns,
                  label: t(
                    "landing.quickActions.compare",
                    "Comparer 2 IA côte à côte"
                  ),
                },
                {
                  id: "ask-pdf",
                  icon: FileText,
                  label: t(
                    "landing.quickActions.askPdf",
                    "Discuter avec un PDF"
                  ),
                },
                {
                  id: "pptx",
                  icon: Presentation,
                  label: t(
                    "landing.quickActions.pptx",
                    "Générer un PowerPoint"
                  ),
                },
                {
                  id: "watch",
                  icon: Eye,
                  label: t("landing.quickActions.watch", "Lancer une veille"),
                },
                {
                  id: "podcast",
                  icon: Headphones,
                  label: t(
                    "landing.quickActions.podcast",
                    "Notes → podcast 2 voix"
                  ),
                },
                {
                  id: "widget",
                  icon: Code2,
                  label: t(
                    "landing.quickActions.widget",
                    "Widget pour ton site"
                  ),
                },
              ].map((chip) => {
                const Icon = chip.icon;
                return (
                  <Link
                    key={chip.id}
                    to={
                      chip.id === "widget"
                        ? "/pricing#business"
                        : `/register?intent=${chip.id}`
                    }
                    className="group inline-flex items-center gap-2 px-3.5 py-2 rounded-full glass border border-white/10 hover:border-primary/40 hover:bg-primary/5 transition-colors text-sm text-slate-300 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    data-testid={`hero-quick-action-${chip.id}`}
                  >
                    <Icon className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                    <span className="whitespace-nowrap">{chip.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Trust badges — credibility signals below the CTA */}
            <div
              className="flex flex-wrap items-center justify-center gap-3 mt-8 text-sm text-slate-400"
              data-testid="hero-trust-badges"
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <span className="text-base">🇪🇺</span>
                {t("landing.trust.eu", "Hébergé en Europe")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <Shield className="w-3.5 h-3.5 text-green-400" />
                {t("landing.trust.gdpr", "RGPD natif")}
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10"
                data-testid="landing-trust-models"
              >
                <Bot className="w-3.5 h-3.5 text-primary" />
                {t("landing.trust.models", "{{count}}+ modèles IA", {
                  count: modelCount.models,
                })}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                {t(
                  "landing.trust.trial",
                  "7 jours d'essai sur tous les plans payants"
                )}
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10"
                data-testid="landing-trust-ai-disclosure"
              >
                <Eye className="w-3.5 h-3.5 text-green-400" />
                {t("landing.trust.aiDisclosure", "Divulgation IA transparente")}
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10"
                data-testid="landing-trust-encrypted"
              >
                <Lock className="w-3.5 h-3.5 text-green-400" />
                {t("landing.trust.encrypted", "Chiffré en transit")}
              </span>
            </div>

            {/* Stats — factual, verifiable product numbers. We deliberately do
                NOT show user/message vanity counts: at pre-launch scale any
                such figure is either tiny or fabricated, and a launch-day
                (HN/PH) audience punishes implausible social proof harder than
                it rewards it (CLAUDE.md §10 gate 5 / §8 — no false claims).
                These three are true and self-updating (models is live from
                /api/models), and they restate the actual thesis: many models,
                one sub · built for 15 languages incl. native Arabic · free with
                no card. */}
            <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto mt-16">
              <div>
                <div className="text-3xl font-bold text-white">
                  {modelCount.models}+
                </div>
                <div className="text-sm text-slate-400">
                  {t("landing.stats.modelsLabel", "Modèles IA, 1 abo")}
                </div>
              </div>
              <div>
                <div
                  className="text-3xl font-bold text-white"
                  data-testid="landing-stat-languages"
                >
                  {LANGUAGE_COUNT}
                </div>
                <div className="text-sm text-slate-400">
                  {t("landing.stats.languagesLabel", "Langues (arabe natif)")}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">€0</div>
                <div className="text-sm text-slate-400">
                  {t("landing.stats.freeLabel", "Pour commencer, sans carte")}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Powered-by AI provider logos — credibility via tech stack */}
        <section
          className="py-10 px-4 border-y border-white/5"
          data-testid="providers-bar"
          aria-label="AI providers powering Stronger Bot"
        >
          <div className="max-w-5xl mx-auto">
            <p className="text-center text-xs uppercase tracking-widest text-slate-400 mb-6">
              {t("landing.poweredBy", "Propulsé par les meilleurs modèles IA")}
            </p>
            {/* Provider list mirrors backend/.env stack + CLAUDE.md §3
                "Providers IA actifs (100% free-tier)". OpenAI / Anthropic
                are deliberately ABSENT — we don't use them (zero-paid
                policy §5, would be misleading to advertise them). The
                providers below are the actual API keys our backend ships
                requests to. GitHub Models + SambaNova removed 2026-08-18
                (endpoint retired / now paywalled); Cerebras removed
                2026-08-30 (free Shared Tier now returns HTTP 402 — no
                longer free, so advertising it would be a false claim
                §7/§5.11). Rendered as glass pills, not a dot-separated
                text list. */}
            <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
              {[
                "GROQ",
                "Google Gemini",
                "Mistral AI",
                "Meta Llama",
                "DeepSeek",
                "Qwen",
                "Cloudflare AI",
                "NVIDIA NIM",
                "Hugging Face",
              ].map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full glass border border-white/10 text-sm font-medium text-slate-300 hover:text-white hover:border-primary/30 transition-colors"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-primary/70"
                    aria-hidden="true"
                  />
                  {name}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Thesis "VS" band — the Ensemble narrative: one locked-in model ($20)
            vs the ensemble (€9). The mockup's signature positioning section. */}
        <section id="thesis" className="thesis-band section-y px-4">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-10">
              <span className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                {t("landing.thesis.eyebrow", "La thèse")}
              </span>
              <h2 className="font-heading text-fluid-h2 font-bold text-foreground mt-3">
                {t("landing.thesis.title")}
              </h2>
              <p className="text-fluid-lead text-muted-foreground mt-4">
                {t("landing.thesis.sub")}
              </p>
            </div>
            <div className="thesis-grid">
              <div className="tcard tcard-dim">
                <span className="font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  {t("landing.thesis.singleLabel")}
                </span>
                <div className="tcard-price mt-2">
                  <s>$20</s>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("landing.thesis.singleCap")}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {["single1", "single2", "single3"].map((k) => (
                    <li
                      key={k}
                      className="flex items-start gap-2.5 text-sm text-muted-foreground"
                    >
                      <X className="w-4 h-4 mt-0.5 shrink-0 opacity-70" />
                      {t(`landing.thesis.${k}`)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="tcard-vs">VS</div>
              <div className="tcard tcard-win">
                <span className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                  {t("landing.thesis.ensembleLabel")}
                </span>
                <div className="tcard-price mt-2">
                  €{PLAN_PRICES.starter}
                  <span className="text-lg font-semibold text-muted-foreground">
                    {" "}
                    {t("landing.thesis.perMo")}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("landing.thesis.ensembleCap", {
                    models: modelCount.models,
                    providers: modelCount.providers,
                  })}
                </p>
                <ul className="mt-5 space-y-2.5">
                  <li className="flex items-start gap-2.5 text-sm text-foreground">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-500" />
                    {t("landing.thesis.ensemble1")}
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-foreground">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-500" />
                    {t("landing.thesis.ensemble2", { langs: LANGUAGE_COUNT })}
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-foreground">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-500" />
                    {t("landing.thesis.ensemble3")}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Catalog "console" — the maquette's signature filterable model board,
            sourced from the honest single-source catalog (models.list). */}
        <CatalogConsoleSection />

        {/* "Le produit" — 2-col band: real capabilities + illustrative chat demo */}
        <ProductShowcaseSection />

        {/* Features Section */}
        <section id="features" className="section-y px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-heading text-fluid-h2 font-bold text-white mb-4">
                {t("landing.features.title")}
              </h2>
              <p className="text-lg text-slate-400 max-w-xl mx-auto">
                {t("landing.features.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="glass lift rounded-2xl p-6 hover:border-primary/40 group"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/25 to-accent/15 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use-case kits showcase — Phase 8 thesis surfaced for anonymous
            visitors. The 6 kits each ship a calibrated system prompt + a
            template library; clicking any card sends the user to /register
            with an intent query so we can route them back to the kit
            after signup. */}
        <KitsShowcaseSection t={t} />

        {/* Universal / multilingual section — live demo card that speaks the
            selected language (LTR & RTL, native Arabic), ringed by 15 language
            chips. The maquette's core differentiator for the maghrébin target. */}
        <UniversalSection />

        {/* Comparison Section */}
        <ComparisonSection t={t} />

        {/* Pricing Section */}
        <section id="pricing" className="section-y px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-heading text-fluid-h2 font-bold text-white mb-4">
                {t("landing.pricing.title")}
              </h2>
              <p className="text-lg text-slate-400 max-w-xl mx-auto">
                {t("landing.pricing.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`glass lift rounded-2xl p-6 relative ${
                    plan.popular ? "border-primary ring-1 ring-primary/30" : ""
                  }`}
                  data-testid={`pricing-card-${index}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-primary-strong text-white text-xs font-medium px-3 py-1 rounded-full">
                        {t("landing.pricing.popular")}
                      </span>
                    </div>
                  )}

                  <h3 className="font-heading text-xl font-semibold text-white mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-white">
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-slate-400">{plan.period}</span>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {(Array.isArray(plan.features) ? plan.features : []).map(
                      (feature, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-slate-300"
                        >
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                          {feature}
                        </li>
                      )
                    )}
                  </ul>

                  <Link to="/register">
                    <Button
                      className={`w-full ${
                        plan.popular
                          ? "btn-glow bg-primary-strong hover:bg-primary-strong/90 text-white"
                          : "bg-primary/10 text-primary border border-primary/25 hover:bg-primary/20"
                      }`}
                      data-testid={`pricing-cta-${index}`}
                    >
                      {index === 0
                        ? t("landing.pricing.start")
                        : t("landing.pricing.choose")}
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-y px-4">
          <div className="max-w-4xl mx-auto text-center glass rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-hero-glow" />
            <div className="relative z-10">
              <h2 className="font-heading text-fluid-h2 font-bold text-white mb-4">
                {t("landing.cta2.title")}
              </h2>
              <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
                {t("landing.cta2.subtitle")}
              </p>
              <Link to="/register">
                <Button
                  size="lg"
                  className="btn-glow bg-primary-strong hover:bg-primary-strong/90 text-lg px-8"
                  data-testid="final-cta"
                >
                  {t("landing.cta")}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section — schema.org FAQPage + visible accordion */}
        <FAQSection t={t} />

        {/* Newsletter Section */}
        <NewsletterSection t={t} />

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-semibold text-white">
                  Stronger Bot
                </span>
              </div>
              <p className="text-sm text-slate-400">{t("landing.footer")}</p>
            </div>
            {/* Live on Google Play (approved 2026-11-03) — the app's public
                store listing, linked from the marketing site so the launch is
                discoverable. The App Store badge joins here once iOS ships. */}
            <div className="flex justify-center mt-6">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="glass inline-flex items-center gap-3 min-h-[44px] px-5 rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                data-testid="play-store-badge"
                aria-label={t(
                  "landing.getItOnGooglePlay",
                  "Disponible sur Google Play"
                )}
              >
                <Smartphone className="w-5 h-5 text-primary" />
                <span className="flex flex-col leading-tight text-left">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">
                    Android
                  </span>
                  <span className="text-sm font-semibold">
                    {t(
                      "landing.getItOnGooglePlay",
                      "Disponible sur Google Play"
                    )}
                  </span>
                </span>
              </a>
            </div>
            {/* Maquette "Ensemble" footer signature: a single stats line built
                from the single-source counts (MODEL_COUNT / PROVIDER_COUNT /
                LANGUAGE_COUNT — never re-hardcoded). The legal links below are
                kept (the maquette omits them, but they are required for store
                compliance §10 + SEO). */}
            <p className="text-center text-xs text-slate-500 mt-5 font-mono">
              {t("landing.footerStats", {
                models: MODEL_COUNT,
                providers: PROVIDER_COUNT,
                languages: LANGUAGE_COUNT,
                defaultValue:
                  "strongerbot.com · {{models}} modèles · {{providers}} fournisseurs · {{languages}} langues",
              })}
            </p>
            <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm text-slate-400">
              <Link
                to="/privacy"
                className="hover:text-white transition-colors"
              >
                {t("legal.nav.privacy")}
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                {t("legal.nav.terms")}
              </Link>
              <Link to="/legal" className="hover:text-white transition-colors">
                {t("legal.nav.legal")}
              </Link>
            </div>
          </div>
        </footer>
      </div>
      <StickyCTABar />
    </>
  );
};

// Use-case kits showcase — surfaces the 6 Phase-8 personas for anonymous
// visitors so they see "a bot for MY job" without needing to sign up first.
// Order matters: PM/Marketer/Founder first (highest-LTV personas based on
// the audience research), Recruiter/Teacher/Freelancer second.
// Catalog "console" — the maquette's signature filterable model board, rendered
// from the SINGLE SOURCE of the honest, actually-served catalog
// (i18n models.list, 14 unique models, already translated 15/15 + prerendered).
// We NEVER hardcode a model list here (§8 no-false-claims: the hero's live "24"
// counts model×provider instances via /api/models; this board shows the deduped
// unique models we truly serve). Filter tabs are derived from the models' own
// `providers` arrays, so adding/removing a model in models.list updates the
// board + its filters automatically. Cards link to the existing /models/:slug
// SEO pages.
const CATALOG_PROVIDERS = [
  { full: "Groq", label: "GROQ" },
  { full: "Google Gemini", label: "Gemini" },
  { full: "Mistral", label: "Mistral" },
  { full: "OpenRouter", label: "OpenRouter" },
  { full: "Cloudflare Workers AI", label: "Cloudflare" },
  { full: "NVIDIA NIM", label: "NVIDIA" },
  { full: "HuggingFace", label: "HuggingFace" },
];

function CatalogConsoleSection() {
  const { t } = useTranslation();
  const localePrefix = useLocalePrefix();
  const [filter, setFilter] = useState("all");
  const rawModels = t("models.list", { returnObjects: true });
  const models = Array.isArray(rawModels) ? rawModels : [];
  // Only surface a provider tab when at least one served model advertises it,
  // so the filter row never offers an empty bucket.
  const tabs = CATALOG_PROVIDERS.filter((p) =>
    models.some((m) => (m.providers || []).includes(p.full))
  );
  const shown =
    filter === "all"
      ? models
      : models.filter((m) => (m.providers || []).includes(filter));

  return (
    <section id="catalog" className="section-y px-4">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-8">
          <span className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
            {t("landing.catalog.eyebrow")}
          </span>
          <h2 className="font-heading text-fluid-h2 font-bold text-foreground mt-3">
            {t("landing.catalog.title")}
          </h2>
          <p className="text-fluid-lead text-muted-foreground mt-4">
            {t("landing.catalog.sub")}
          </p>
        </div>

        {/* Provider filter tabs */}
        <div
          className="flex flex-wrap gap-2 mb-8"
          role="group"
          aria-label={t("landing.catalog.title")}
          data-testid="catalog-filters"
        >
          <button
            type="button"
            onClick={() => setFilter("all")}
            aria-pressed={filter === "all"}
            className="lang-chip"
          >
            {t("landing.catalog.all")}
          </button>
          {tabs.map((p) => (
            <button
              key={p.full}
              type="button"
              onClick={() => setFilter(p.full)}
              aria-pressed={filter === p.full}
              className="lang-chip"
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Model cards — deduped unique models we actually serve */}
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="catalog-grid"
        >
          {shown.map((m) => (
            <Link
              key={m.slug}
              to={`${localePrefix}/models/${m.slug}`}
              className="glass lift rounded-2xl p-5 hover:border-primary/40 group block"
            >
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-xs font-medium text-primary uppercase tracking-wider">
                  {m.developer}
                </span>
                <span className="text-[10px] font-semibold text-muted-foreground border border-white/10 rounded-full px-2 py-0.5">
                  {m.kind}
                </span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                {m.name}
              </h3>
              <p className="font-mono text-[11px] text-muted-foreground mt-1 truncate">
                {m.slug}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(m.providers || []).map((prov) => (
                  <span
                    key={prov}
                    className="text-[10px] text-muted-foreground bg-white/5 border border-white/10 rounded-full px-2 py-0.5"
                  >
                    {prov}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to={`${localePrefix}/models`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
            data-testid="catalog-view-all"
          >
            {t("landing.catalog.viewAll")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// "Le produit" — the maquette's 2-column product band: three REAL capabilities
// on the left (web search + citations, document/PDF analysis, per-trade kits —
// all live features), and a stylized chat-demo mockup on the right that shows
// two of them in motion (grounded web answer with source chips, then a one-tap
// switch to a faster model). The demo is an illustration (obviously a mockup
// card), not a live transcript; the capabilities it depicts are genuinely
// shipped (web grounding + citations, model switch), so it's honest (§8).
function ProductShowcaseSection() {
  const { t } = useTranslation();
  const caps = [
    {
      icon: Globe,
      title: t("landing.product.webTitle"),
      desc: t("landing.product.webDesc"),
    },
    {
      icon: FileText,
      title: t("landing.product.docsTitle"),
      desc: t("landing.product.docsDesc"),
    },
    {
      icon: Briefcase,
      title: t("landing.product.kitsTitle"),
      desc: t("landing.product.kitsDesc"),
    },
  ];
  return (
    <section id="product" className="section-y px-4">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left — real capabilities */}
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
            {t("landing.product.eyebrow")}
          </span>
          <h2 className="font-heading text-fluid-h2 font-bold text-foreground mt-3 mb-8">
            {t("landing.product.title")}
          </h2>
          <ul className="space-y-6">
            {caps.map((c) => (
              <li key={c.title} className="flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-primary/25 to-accent/15 border border-white/10 flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-primary" />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    {c.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mt-1">
                    {c.desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right — illustrative chat-demo mockup */}
        <div
          className="glass rounded-2xl border border-white/10 shadow-xl p-4 sm:p-5"
          data-testid="product-demo"
        >
          {/* chrome bar */}
          <div className="flex items-center gap-2 mb-4">
            <span className="flex gap-1.5" aria-hidden="true">
              <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
              <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
              <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground border border-white/10 rounded-lg px-2.5 py-1">
              llama-4-maverick
            </span>
          </div>

          {/* user q1 */}
          <div className="flex justify-end mb-3">
            <p className="max-w-[85%] bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
              {t("landing.product.demoQ1")}
            </p>
          </div>
          {/* assistant a1 */}
          <div className="rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 px-4 py-3 mb-4">
            <p className="font-mono text-[11px] text-muted-foreground mb-1.5">
              → Llama 4 · {t("landing.product.demoTag1")}
            </p>
            <p className="text-sm text-foreground leading-relaxed">
              {t("landing.product.demoA1")}
            </p>
            <div className="flex gap-2 mt-2.5">
              {[1, 2].map((n) => (
                <span
                  key={n}
                  className="inline-flex items-center gap-1 text-[11px] text-primary bg-primary/10 rounded-full px-2 py-0.5"
                >
                  <LinkIcon className="w-3 h-3" aria-hidden="true" />
                  {t("landing.product.demoSource")} {n}
                </span>
              ))}
            </div>
          </div>

          {/* user q2 */}
          <div className="flex justify-end mb-3">
            <p className="max-w-[85%] bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
              {t("landing.product.demoQ2")}
            </p>
          </div>
          {/* assistant a2 (switched) */}
          <div className="rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 px-4 py-3">
            <p className="font-mono text-[11px] text-muted-foreground mb-1.5 inline-flex items-center gap-1">
              <Zap className="w-3 h-3 text-accent" aria-hidden="true" />
              {t("landing.product.demoSwitch")} gpt-oss-120b · GROQ
            </p>
            <p className="text-sm text-foreground leading-relaxed">
              {t("landing.product.demoA2")}{" "}
              <Sparkles
                className="inline w-3.5 h-3.5 text-accent"
                aria-hidden="true"
              />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function UniversalSection() {
  const { t, i18n } = useTranslation();
  const [sel, setSel] = useState(
    languages.find((l) => l.code === i18n.language)?.code || "fr"
  );
  const active = languages.find((l) => l.code === sel) || languages[1];
  const sample = LANG_SAMPLES[sel] || LANG_SAMPLES.fr;
  return (
    <section id="langues" className="section-y px-4">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-10">
          <span className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
            {t("landing.universal.eyebrow", "Universel · 15 langues")}
          </span>
          <h2 className="font-heading text-fluid-h2 font-bold text-foreground mt-3">
            {t("landing.universal.title")}
          </h2>
          <p className="text-fluid-lead text-muted-foreground mt-4">
            {t("landing.universal.sub")}
          </p>
        </div>
        <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-6 items-start">
          <div
            className="demo-card"
            dir={active.dir}
            data-testid="universal-demo"
          >
            <div className="demo-who">
              <span aria-hidden="true">{active.flag}</span>
              {active.name}
            </div>
            <p className="demo-sample">{sample}</p>
          </div>
          <div
            className="flex flex-wrap gap-2 content-start"
            role="group"
            aria-label={t("landing.universal.eyebrow", "Langues")}
          >
            {languages.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => setSel(l.code)}
                aria-pressed={sel === l.code}
                className="lang-chip"
              >
                <span aria-hidden="true">{l.flag}</span>
                {l.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function KitsShowcaseSection({ t }) {
  const kits = [
    {
      id: "pm",
      icon: Briefcase,
      name: t("landing.kits.pm.name", "Project Manager"),
      tagline: t(
        "landing.kits.pm.tagline",
        "Stand-ups, status reports, RAID, sprints — en un clic."
      ),
    },
    {
      id: "marketer",
      icon: Megaphone,
      name: t("landing.kits.marketer.name", "Marketer"),
      tagline: t(
        "landing.kits.marketer.tagline",
        "Landing copy, emails, ads, SEO — clarté > clinquant."
      ),
    },
    {
      id: "founder",
      icon: Rocket,
      name: t("landing.kits.founder.name", "Founder"),
      tagline: t(
        "landing.kits.founder.tagline",
        "Investor updates, OKRs, board decks — direct, chiffré."
      ),
    },
    {
      id: "recruiter",
      icon: Users,
      name: t("landing.kits.recruiter.name", "Recruteur·se"),
      tagline: t(
        "landing.kits.recruiter.tagline",
        "Fiches de poste, debrief scorecards, offres — hiring équitable."
      ),
    },
    {
      id: "teacher",
      icon: GraduationCap,
      name: t("landing.kits.teacher.name", "Enseignant·e"),
      tagline: t(
        "landing.kits.teacher.tagline",
        "Séquences, différenciation, rubrics — pédagogie fondée sur preuves."
      ),
    },
    {
      id: "freelancer",
      icon: Code2,
      name: t("landing.kits.freelancer.name", "Freelance"),
      tagline: t(
        "landing.kits.freelancer.tagline",
        "Devis, prospection, relances — protège ton TJM et ton temps."
      ),
    },
  ];

  return (
    <section
      id="kits"
      className="section-y px-4"
      data-testid="landing-kits-section"
      aria-labelledby="kits-title"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-4">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs uppercase tracking-wider text-slate-400">
              {t("landing.kits.badge", "Kits métier")}
            </span>
          </div>
          <h2
            id="kits-title"
            className="font-heading text-fluid-h2 font-bold text-white mb-4"
          >
            {t("landing.kits.title", "Un bot pré-configuré pour ton métier")}
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t(
              "landing.kits.subtitle",
              "Système-prompts experts + 8 templates prêts à coller par profession. 1 click et le chat parle déjà ta langue métier."
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {kits.map((kit) => {
            const Icon = kit.icon;
            return (
              <Link
                key={kit.id}
                to={`/register?intent=kit-${kit.id}`}
                className="glass lift rounded-2xl p-5 border border-white/10 hover:border-primary/40 hover:bg-primary/5 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid={`landing-kit-${kit.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/25 transition-colors">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-white">
                      {t("landing.kits.botFor", "Bot for {{name}}", {
                        name: kit.name,
                      })}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {kit.tagline}
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary group-hover:gap-2 transition-all">
                  {t("landing.kits.cta", "Essayer")}
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          {t(
            "landing.kits.footnote",
            "Plus de kits arrivent : Designer · Support · Sales · Customer Success"
          )}
        </p>
      </div>
    </section>
  );
}

function ComparisonSection({ t }) {
  // Live count from /api/models — hook is cached in sessionStorage so
  // re-using it here doesn't cost a second round trip when LandingPage
  // already fetched. Fallback matches the trust-bar (29/10).
  const modelCount = useModelCount({
    models: MODEL_COUNT,
    providers: PROVIDER_COUNT,
  });
  const Y = () => (
    <Check className="w-4 h-4 text-green-400 mx-auto" aria-label="yes" />
  );
  const N = () => (
    <X className="w-4 h-4 text-slate-600 mx-auto" aria-label="no" />
  );
  const cols = [
    {
      key: "sb",
      name: "Stronger Bot",
      price: `€${PLAN_PRICES.pro}`,
      highlight: true,
    },
    { key: "chatgpt", name: "ChatGPT Plus", price: "$20" },
    { key: "claude", name: "Claude Pro", price: "$20" },
    { key: "gemini", name: "Gemini Adv.", price: "$20" },
    { key: "mistral", name: "Le Chat Pro", price: "€15" },
  ];
  const rows = [
    {
      label: t("landing.comparison.row_models", "Modèles IA inclus"),
      sb: `${modelCount.models}+`,
      chatgpt: "1",
      claude: "1",
      gemini: "1",
      mistral: "1",
    },
    {
      label: t("landing.comparison.row_images", "Génération d'images"),
      sb: <Y />,
      chatgpt: <Y />,
      claude: <N />,
      gemini: <Y />,
      mistral: <N />,
    },
    {
      label: t("landing.comparison.row_voice", "Mode vocal"),
      sb: <Y />,
      chatgpt: <Y />,
      claude: <N />,
      gemini: <N />,
      mistral: <N />,
    },
    {
      label: t("landing.comparison.row_rag", "Analyse de documents"),
      sb: <Y />,
      chatgpt: <Y />,
      claude: <Y />,
      gemini: <Y />,
      mistral: <Y />,
    },
    {
      label: t("landing.comparison.row_agents", "Agents personnalisés"),
      sb: <Y />,
      chatgpt: <Y />,
      claude: <N />,
      gemini: <N />,
      mistral: <N />,
    },
    {
      label: t("landing.comparison.row_langs", "Interface multilingue"),
      sb: "15",
      chatgpt: "10+",
      claude: "10+",
      gemini: "10+",
      mistral: "10+",
    },
    {
      label: t("landing.comparison.row_api", "Accès API inclus"),
      sb: <Y />,
      chatgpt: <N />,
      claude: <N />,
      gemini: <N />,
      mistral: <N />,
    },
    {
      // ChatGPT a introduit de la pub dans son free tier (10/2026) ; SB non.
      label: t("landing.comparison.row_noads", "Sans publicité"),
      sb: <Y />,
      chatgpt: <N />,
      claude: <Y />,
      gemini: <Y />,
      mistral: <Y />,
    },
    {
      label: t(
        "landing.comparison.row_sidebyside",
        "Comparer 2 modèles côte-à-côte"
      ),
      sb: <Y />,
      chatgpt: <N />,
      claude: <N />,
      gemini: <N />,
      mistral: <N />,
    },
    {
      label: t("landing.comparison.row_docgen", "Export .pptx / .docx / .xlsx"),
      sb: <Y />,
      chatgpt: <N />,
      claude: <N />,
      gemini: <N />,
      mistral: <N />,
    },
    {
      label: t(
        "landing.comparison.row_widget",
        "Widget embarquable sur ton site"
      ),
      sb: <Y />,
      chatgpt: <N />,
      claude: <N />,
      gemini: <N />,
      mistral: <N />,
    },
  ];

  return (
    <section
      id="comparison"
      className="section-y px-4"
      data-testid="comparison-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-fluid-h2 font-bold text-white mb-4">
            {t("landing.comparison.title", "Pourquoi Stronger Bot ?")}
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t(
              "landing.comparison.subtitle",
              "Une seule souscription, toutes les IA. Plus besoin d'empiler ChatGPT + Claude + Gemini + Mistral."
            )}
          </p>
          <p
            className="text-sm text-slate-400 max-w-2xl mx-auto mt-4"
            data-testid="comparison-stack-cost"
          >
            {t(
              "landing.comparison.stackCost",
              "Pour égaler la couverture de Stronger Bot ailleurs : ChatGPT Plus + Claude Pro + Gemini Advanced + Le Chat Pro ≈ €72/mois — soit 3,8× plus cher."
            )}
          </p>
        </div>

        <div className="glass rounded-2xl overflow-x-auto">
          <table className="w-full text-sm text-slate-300">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-4 font-normal text-slate-400">
                  {t("landing.comparison.feature_col", "Fonctionnalité")}
                </th>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    className={`px-4 py-4 text-center font-heading ${
                      c.highlight ? "text-primary bg-primary/5" : "text-white"
                    }`}
                  >
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-slate-400 font-normal mt-1">
                      {c.price}/mo
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-3 text-slate-400">{r.label}</td>
                  {cols.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-3 text-center ${
                        c.highlight ? "bg-primary/5 font-medium text-white" : ""
                      }`}
                    >
                      {r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          {t(
            "landing.comparison.disclaimer",
            "Comparaison indicative basée sur les offres publiques des concurrents (avril 2026). Tarifs USD convertis à ~1$ = 0,95€."
          )}
        </p>
      </div>
    </section>
  );
}

function FAQSection({ t }) {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    {
      q: t("landing.faq.q1", "Qu'est-ce que Stronger Bot ?"),
      a: t(
        "landing.faq.a1",
        "Une plateforme IA tout-en-un qui regroupe " +
          MODEL_COUNT +
          "+ modèles libres (Llama 3.3, Llama 4, Gemini, Mistral, DeepSeek, Qwen, GPT-OSS...) dans un seul abonnement, répartis sur " +
          PROVIDER_COUNT +
          " providers. Au lieu d'empiler des souscriptions à $20/mois chez chaque fournisseur, tu paies €19/mois pour tous les avoir."
      ),
    },
    {
      q: t("landing.faq.q2", "Est-ce gratuit ?"),
      a: t(
        "landing.faq.a2",
        "Oui, le plan Free inclut 50 messages/mois, 2 documents et le chat IA basique. Sans carte bancaire. Tu passes sur un plan payant (Starter €9, Pro €19, Business €39) seulement si tu as besoin de plus."
      ),
    },
    {
      q: t("landing.faq.q3", "En quoi Stronger Bot est différent de ChatGPT ?"),
      a: t(
        "landing.faq.a3",
        "ChatGPT te donne 1 modèle (GPT-4o) pour $20/mois. Stronger Bot t'en donne " +
          MODEL_COUNT +
          "+ pour €19/mois, avec génération d'images, mode vocal, analyse de documents, agents personnalisés et accès API inclus."
      ),
    },
    {
      q: t("landing.faq.q4", "Mes données sont-elles protégées ?"),
      a: t(
        "landing.faq.a4",
        "Oui. L'application est hébergée en Europe (Cloudflare Pages) et respecte le RGPD nativement. Tes conversations et documents ne servent jamais à entraîner des modèles. Tu peux exporter ou supprimer toutes tes données à tout moment."
      ),
    },
    {
      q: t("landing.faq.q5", "Est-ce que ça marche sur mobile ?"),
      a: t(
        "landing.faq.a5",
        "Oui, l'application web fonctionne sur tout navigateur mobile et peut s'installer comme une PWA. Les apps natives iOS et Android arrivent sur l'App Store et le Play Store."
      ),
    },
    {
      q: t("landing.faq.q6", "Y a-t-il une période d'essai ?"),
      a: t(
        "landing.faq.a6",
        "Oui, tous les plans payants incluent 7 jours d'essai gratuit. Tu n'es facturé qu'à la fin de la période, et tu peux annuler à tout moment avant."
      ),
    },
  ];

  return (
    <section
      id="faq"
      className="section-y-sm px-4"
      data-testid="faq-section"
      aria-labelledby="faq-title"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2
            id="faq-title"
            className="font-heading text-fluid-h2 font-bold text-white mb-4"
          >
            {t("landing.faq.title", "Questions fréquentes")}
          </h2>
          <p className="text-lg text-slate-400">
            {t(
              "landing.faq.subtitle",
              "Tout ce qu'il faut savoir avant de s'inscrire."
            )}
          </p>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className="glass rounded-2xl overflow-hidden transition-all"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white">{f.q}</span>
                  <span
                    className={`text-slate-400 transition-transform ${
                      isOpen ? "rotate-45" : ""
                    }`}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-slate-400 leading-relaxed text-sm">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection({ t }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus("success");
        setMessage(data.message || "Inscription confirmee !");
        setEmail("");
      } else {
        setStatus("error");
        setMessage(data.detail || "Erreur, reessayez.");
      }
    } catch {
      setStatus("error");
      setMessage("Service indisponible.");
    }
  };

  return (
    <section className="section-y-sm px-4">
      <div className="max-w-2xl mx-auto text-center glass rounded-3xl p-10">
        <Mail className="w-10 h-10 text-primary mx-auto mb-4" />
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
          {t("landing.newsletter.title", "Restez informe")}
        </h2>
        <p className="text-slate-400 mb-6">
          {t(
            "landing.newsletter.subtitle",
            "Recevez nos actualites IA, astuces et mises a jour. Pas de spam."
          )}
        </p>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("landing.newsletter.placeholder", "votre@email.com")}
            aria-label={t(
              "landing.newsletter.ariaLabel",
              "Adresse email pour la newsletter"
            )}
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
            data-testid="newsletter-email"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="px-6 py-3 rounded-xl bg-primary-strong hover:bg-primary-strong/90 text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="newsletter-submit"
          >
            {status === "loading" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t("landing.newsletter.cta", "S'inscrire")
            )}
          </button>
        </form>
        {status === "success" && (
          <p
            className="mt-4 text-green-400 text-sm"
            data-testid="newsletter-success"
          >
            {message}
          </p>
        )}
        {status === "error" && (
          <p
            className="mt-4 text-red-400 text-sm"
            data-testid="newsletter-error"
          >
            {message}
          </p>
        )}
      </div>
    </section>
  );
}

export default LandingPage;
